import { useMemo, useEffect, useRef } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import Sidebar from './Sidebar'
import Header from './Header'
import Breadcrumbs from '@components/common/Breadcrumbs'
import useKeyboardShortcuts from '@hooks/useKeyboardShortcuts'
import useObjectTypes from '@hooks/useObjectTypes'
import { REPORTS_ONLY_MODE } from '@config/featureFlags'

/**
 * `<input type>` values that are NOT free-text entry, so a route change may take
 * focus off them. Everything else an `<input>` can be — text, search, email, url,
 * tel, number, password, and an unknown/absent type (which the browser treats as
 * text) — is typing, and typing must not be interrupted. Listing the exceptions
 * rather than the text types is deliberate: a new text-ish type added to HTML
 * defaults to being protected.
 */
const NON_TEXT_INPUT_TYPES = new Set([
  'button', 'checkbox', 'color', 'file', 'hidden', 'image',
  'radio', 'range', 'reset', 'submit',
])

const Layout = () => {
  const location = useLocation()
  const mainRef = useRef(null)

  // Load object types into appStore here (not in the Sidebar) so the Objects page
  // / ObjectList work even when the sidebar is hidden (reports-only mode, spec 032).
  useObjectTypes()

  const shortcuts = useMemo(() => [
    {
      key: 'k',
      meta: true,
      handler: () => {
        const input = document.querySelector('[data-search-input]')
        if (input) input.focus()
      },
    },
  ], [])

  useKeyboardShortcuts(shortcuts)

  /*
   * Reset focus to main content on route change for screen readers.
   *
   * EXCEPT when the navigation was caused by TYPING into persistent chrome that
   * survived it (QA, 2026-09-03). The header's report search navigates on the
   * first keystroke — `/reports/190` → `/reports?q=b` (`Header.jsx:65`) — so this
   * effect fired and yanked focus into `<main>`, and the user was thrown out of
   * the box after exactly one character. Every LATER keystroke stays on
   * `/reports` and only touches the query string, so `location.pathname` doesn't
   * change and focus survives; that asymmetry is why the symptom was "the first
   * character only".
   *
   * Narrowed to text entry on purpose, rather than to "anything still focused":
   *  - Typing is the one case where moving focus destroys work in progress, and
   *    the user is unambiguously still where they meant to be.
   *  - A LINK in the persistent header/sidebar is a click, and moving focus to
   *    main is the announcement a screen-reader user should get from it — so that
   *    case must keep resetting. A blanket "is it still in the document" check
   *    would have silently dropped it for every chrome nav link.
   *
   * `isConnected` is the load-bearing half: a search box inside the page that
   * unmounted with the old route is gone, and main should get focus then.
   */
  useEffect(() => {
    const el = mainRef.current
    if (!el) return
    const active = document.activeElement
    if (active && active !== document.body && active.isConnected && !el.contains(active)) {
      const tag = active.tagName
      const isTextEntry = tag === 'TEXTAREA'
        || active.isContentEditable
        || (tag === 'INPUT' && !NON_TEXT_INPUT_TYPES.has((active.getAttribute('type') || 'text').toLowerCase()))
      if (isTextEntry) return
    }
    el.focus({ preventScroll: true })
  }, [location.pathname])

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      <a href="#main-content" className="skip-to-main">
        Skip to main content
      </a>
      {/* Reports-only mode hides the left nav (spec 032); the brand/home anchor
          moves into the Header. Other pages stay reachable by direct URL. */}
      {!REPORTS_ONLY_MODE && <Sidebar />}
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header />
        <Breadcrumbs />
        {/*
          * <main> is BOTH the 24px gutter owner and the app's scroll container,
          * and that has one measurable consequence worth writing down rather
          * than "fixing" — A1, 2026-08-26.
          *
          * The classic scrollbar takes its ~15px off main's content box, on the
          * right only. Header and Breadcrumbs above are siblings of main, not
          * children, so they never lose it. Result, at 1920px with a report on
          * screen: at 1280px every band's content starts on the same left edge
          * (90px), and the report's visual RIGHT gutter measures ~105px against
          * that 90px left. A screenshot diff will show the 15px; it is the
          * scrollbar, not a broken gutter rule, and it is unchanged by the A1
          * increase (that moved both edges by 50px, not the gap between them).
          *
          * Above ~1823px it also costs 7.5px of LEFT-edge alignment. Preflight
          * makes every box border-box, so .report-shell's 1760px cap bites on
          * main's content box (W - 48 - 15) at W > 1823 and on the bands' (W -
          * 48) at W > 1808; past that both columns are centred by margin-inline
          * auto in containers 15px apart, so the bands sit half of that — 7.5px
          * — to the right of the report. Measured at 1920px: bands 146px, report
          * 138.5px. It was 66px before this change, so it is a large improvement
          * and not a clean zero; only restructuring this element gets the zero.
          *
          * Not restructured deliberately: moving the padding to an inner wrapper
          * or the scroll to an outer one would put the scrollbar in a different
          * place on every one of the ~25 non-report pages to buy 15px on one.
          */}
        <main
          ref={mainRef}
          id="main-content"
          tabIndex={-1}
          className="flex-1 overflow-y-auto p-6 outline-none"
        >
          <Outlet />
        </main>
      </div>
    </div>
  )
}

export default Layout
